import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
  TUser,
} from 'evolve-ui';
import {
  ApprovedData,
  Author,
  CreatedData,
  PendingData,
  RejectedData,
  TAudit,
} from './audit-types';
import { TCustomer } from './digital-signatures.api';

export enum TCustomerStatus {
  POTENTIAL = 1,
  ACTIVE = 2,
  INACTIVE = 3,
  EXCLUDED = 4,
}

export type TPaymentType = {
  id: string;
  portalId: string;
  name: string;
  numberDays: string;
};

export type TInformaDbSearchHit = {
  duns: string;
  vat: string;
  name: string;
  alreadyExists: boolean;
  mongoId?: string;
  customerStatus?: TCustomerStatus;
  found: string;
  address: string;
  district: string;
  county: string;
  locality: string;
  postalCode: string;
  state: string;
  stateCode: string;
  companyType?: string;
  officeType?: string;
  legalForm?: string;
  streetType?: string;
  street?: string;
  streetNumber?: string;
};

export type TCompanyInformaDbData = {
  duns: string;
  vat: string;
  name: string;
  headquarters: {
    type: string;
    street: string;
    streetNumber: string;
    postalCode: string;
    postalAddress: string;
    locality: string;
    county: string;
    district?: string;
  };
  mainAddress: {
    type: string;
    street: string;
    streetNumber: string;
    postalCode: string;
    postalAddress: string;
    locality: string;
    county: string;
    //district: string;
  };
  cae: {
    portalId: string;
    code: string;
    name: string;
    activitySector: TActivitySector;
  };
  totalEmployees: number;
  phone: string;
  email: string;
  website: string;
  riskIndicator: number; // 0–10 (Informa); 0 = unknown
  sales: number;
  socialCapital: number;
};

export type TActivitySector = {
  id: string;
  portalId: string;
  name: string;
};
export type TCAE = {
  id: string;
  portalId: string;
  code: string;
  name: string;
  activitySector: TActivitySector;
};

// Customer Regulations Table
export type TIRCT = {
  id: string;
  portalId: string;
  name: string;
};

export type TAnadoluMongoCustomer = {
  id: string;
  portalId?: number;
  name?: string;
  status?: TCustomerStatus;
  fiscalNumber?: string;
  isForeign?: boolean;
  isImportedFromInforma?: boolean;
  socialSecurityNumber?: string;
  numberOfDirectWorkers?: string;
  website?: string;
  phone?: string;
  email?: string;
  observations?: string;
  excludedMotive?: string;
  riskIndicator?: number;
  linkedToMe?: boolean;
  linkedToMyAgencies?: boolean;
  lastInitiativeDate?: string;
  cae?: TCAE;
  paymentType?: TPaymentType;
  customerRegulation?: TIRCT;
  responsibleCharges?: TUser;
  addresses?: Array<{
    id?: string;
    portalId?: number;
    name?: string;
    isMain?: boolean;
    isInvoice?: boolean;
    address?: string;
    locality?: string;
    county?: string;
    zipcode?: string;
    email?: string;
    phone?: string;
    country?: TPortalCountry;
  }>;
  modifiedAt?: Date | string;
  modifiedBy?: TUser & {
    profileId?: number;
    agencyId?: number;
    isAdmin?: boolean;
  };
};

export type TCustomerType = {
  //id: string;
  portalId: number;
  name: string;
  details: string;
};

export type TPortalCountry = {
  id: string;
  code: string;
  name: string;
};

export type TCustomerAddress = {
  id: string;
  portalId: string;
  name: string;
  isMain: boolean;
  isInvoice: boolean;
  isImportedFromInforma?: boolean;
  address: string;
  country: {
    portalId: string;
    name: string;
  };
  county: string;
  district?: string;
  locality: string;
  zipcode: string;
  phone?: string;
  email?: string;
};

export type TMongoCustomer = {
  id: string;
  name: string;
  number?: string;
  portalId?: string;
  status?: TCustomerStatus;
  code?: string;
  customerType?: TCustomerType;
  website?: string;
  fiscalNumber?: string;
  isForeign?: boolean;
  socialSecurityNumber?: string;
  cae?: TCAE;
  numberOfDirectWorkers?: number;
  numberOfTemporaryWorkers?: number;
  paymentType?: TPaymentType;
  responsibleCharges?: TUser;
  manager?: TUser;
  customerRegulation?: TIRCT;
  observations?: string;
  validated?: boolean;
  hasFormationAgreement?: boolean;
  customerStatusActiveDate?: string;
  isImportedFromInforma?: boolean;
  addresses?: TCustomerAddress[];
  phone?: string;
  email?: string;
  sales?: number;
  socialCapital?: number;
  /** 0–10 (Informa); 0 = unknown */
  riskIndicator?: number;
  createdAt?: string;
  updatedAt?: string;
  modifiedAt?: Date;
  modifiedBy?: TUser & { isAdmin?: boolean };
  hasPendingChanges?: boolean;
  lastInitiativeDate?: Date;
  linkedToMyAgencies?: boolean;
  linkedToMe?: boolean;
  /** When present, agencies where the current user has this customer in "Meus Clientes". */
  myAgenciesLinked?: number[];
};

export type TCustomerAudit = TAudit & {
  data: PendingData | ApprovedData | RejectedData | CreatedData;
};

export type TCustomerLegacyFile = {
  name: string;
  size: number;
  mtime: string;
  diskPath?: string;
};

export enum TCustomerDocumentType {
  DOCUMENT = 'document',
  LOGO = 'logo',
}

export type TCustomerDocument = {
  _id: string;
  name: string;
  link: string;
  type?: TCustomerDocumentType;
  uploadedAt?: string;
  uploadedBy?: Author;
};

export type TCustomerDocumentsList = {
  legacy: TCustomerLegacyFile[];
  s3: TCustomerDocument[];
};

/** Row from Mongo `informa_user_notifications` (daily cron snapshot). */
export type TInformaSyncedNotification = {
  id: string;
  alertDay: string;
  startDate?: string;
  endDate?: string;
  syncedAt?: string;
  source?: string;
  identifierType?: string;
  identifierValue?: string;
  name?: string;
  date?: string;
  type?: string;
  reference?: string | null;
  subWallet?: string | null;
  serviceLevel?: string;
  text?: string | null;
  trigger?: string;
  subTrigger?: string;
  reportProduct?: string;
  notificationProduct?: string;
};

export const customersApi = createApi({
  reducerPath: 'customers',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_CUSTOMERS_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: [
    'InformaDbSearchHit',
    'Customer',
    'AnadoluCustomer',
    'Audits',
    'Addresses',
    'CustomerDocuments',
  ],
  endpoints: (builder) => ({
    createCustomer: builder.mutation<Response<TCustomer>, { customer: any }>({
      query: ({ customer }) => ({
        url: `customers`,
        method: 'POST',
        body: customer,
      }),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TCustomer>(
          res as JsonApiResponseDto<TCustomer>,
          meta,
          args,
          true,
        ),
    }),
    getCustomer: builder.query<
      Response<TMongoCustomer>,
      { customerId: string }
    >({
      providesTags: ['Customer'],
      query: ({ customerId }) => ({
        url: `customers/${customerId}`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TMongoCustomer>,
    }),
    getCustomerByNif: builder.query<
      Response<TMongoCustomer>,
      { customerNif: string }
    >({
      providesTags: ['Customer'],
      query: ({ customerNif }) => ({
        url: `customers/nif/${customerNif}`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TMongoCustomer>,
    }),
    getCustomersList: builder.query<Response<TMongoCustomer>, TQueryParams>({
      providesTags: ['Customer'],
      query: (params) => formatQueryParams(params, 'customers'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TMongoCustomer>(
          res as JsonApiResponseDto<TMongoCustomer>,
          meta,
          args,
          true,
        ),
    }),
    editCustomer: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string; customer: any }
    >({
      query: ({ customerId, customer }) => ({
        invalidatesTags: ['Customer'],
        url: `customers/${customerId}`,
        method: 'PATCH',
        body: customer,
      }),
      transformResponse: parseJsonApiResponse<TMongoCustomer>,
    }),
    updateCustomerFiscalNumber: builder.mutation({
      invalidatesTags: ['Customer'],
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/nif`,
        method: 'PATCH',
        body,
      }),
    }),
    validateCustomer: builder.mutation({
      invalidatesTags: ['Customer'],
      query: ({ customerId }) => ({
        url: `customers/${customerId}/validate`,
        method: 'POST',
      }),
    }),
    activateCustomer: builder.mutation({
      invalidatesTags: ['Customer'],
      query: ({ customerId }) => ({
        url: `customers/${customerId}/activate`,
        method: 'POST',
      }),
    }),
    changeCustomerStatus: builder.mutation({
      invalidatesTags: ['Customer'],
      query: ({ customerId, status, excludedMotiveReason }) => ({
        url: `customers/${customerId}/status`,
        method: 'PATCH',
        body: { status, excludedMotiveReason },
      }),
    }),
    excludeCustomer: builder.mutation({
      invalidatesTags: ['Customer'],
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/exclude`,
        method: 'POST',
        body,
      }),
    }),
    syncCustomerWithInforma: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string }
    >({
      invalidatesTags: ['Customer'],
      query: ({ customerId }) => ({
        url: `customers/${customerId}/sync-with-informa`,
        method: 'POST',
      }),
      transformResponse: parseJsonApiResponse<TMongoCustomer>,
    }),

    searchCustomers: builder.query<Response<TInformaDbSearchHit>, TQueryParams>(
      {
        providesTags: ['InformaDbSearchHit'],
        query: (query) => formatQueryParams(query, `/informadb/search`),
        transformResponse: (res, meta, args) => {
          return parseJsonApiResponse<TInformaDbSearchHit>(
            res as JsonApiResponseDto<TInformaDbSearchHit>,
            meta,
            args,
            false,
          );
        },
      },
    ),
    importInformaDbCustomer: builder.mutation<
      Response<TCompanyInformaDbData>,
      { nif: string }
    >({
      query: ({ nif }) => ({
        url: `informadb/${nif}`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TCompanyInformaDbData>,
    }),
    getCustomerRecord: builder.mutation({
      queryFn: async (
        { customerId, language },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `customer-record/${customerId}/${language}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);
          //const type = result.data.type.split('/')[1];
          hiddenElement.href = blobPDF;
          hiddenElement.target = '_blank';
          //hiddenElement.download = `${documentId}.${type}`;
          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
    getInformaReport: builder.mutation({
      queryFn: async ({ nif }, api, extraOptions, baseQuery) => {
        const result: any = await baseQuery({
          url: `informadb/report?nif=${nif}`,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        });
        try {
          var hiddenElement = document.createElement('a');
          var url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          var blobPDF = url.createObjectURL(result.data);
          //const type = result.data.type.split('/')[1];
          hiddenElement.href = blobPDF;
          hiddenElement.target = '_blank';
          //hiddenElement.download = `${documentId}.${type}`;
          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
    // Addresses
    editCustomerAddress: builder.mutation<
      Response<TCustomerAddress>,
      { customerId: string; addressId: string; address: any }
    >({
      invalidatesTags: ['Customer', 'Addresses'],
      query: ({ customerId, addressId, address }) => ({
        invalidatesTags: ['Customer'],
        url: `customers/${customerId}/addresses/${addressId}`,
        method: 'PATCH',
        body: address,
      }),
      transformResponse: parseJsonApiResponse<TCustomerAddress>,
    }),
    deleteCustomerAddress: builder.mutation<
      Response<TCustomerAddress>,
      { customerId: string; addressId: string }
    >({
      invalidatesTags: ['Customer', 'Addresses'],
      query: ({ customerId, addressId }) => ({
        url: `customers/${customerId}/addresses/${addressId}`,
        method: 'DELETE',
      }),
      transformResponse: parseJsonApiResponse<TCustomerAddress>,
    }),
    addCustomerAddress: builder.mutation<
      Response<TCustomerAddress>,
      { customerId: string; address: any }
    >({
      invalidatesTags: ['Customer', 'Addresses'],
      query: ({ customerId, address }) => ({
        url: `customers/${customerId}/addresses`,
        method: 'POST',
        body: address,
      }),
      transformResponse: parseJsonApiResponse<TCustomerAddress>,
    }),
    // Logs
    getCustomerLogs: builder.query<
      Response<TCustomerAudit>,
      TQueryParams & { customerId: string }
    >({
      providesTags: ['Audits'],
      query: ({ customerId, ...params }) =>
        formatQueryParams({ ...params }, `customers/${customerId}/audit`),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TCustomerAudit>(
          res as JsonApiResponseDto<TCustomerAudit>,
          meta,
          args,
          true,
        ),
    }),
    acceptPendingCustomerAudit: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string; body?: { fieldsToAccept?: any[] } }
    >({
      invalidatesTags: ['Customer', 'Audits'],
      query: ({ customerId, body }) => ({
        url: `customers/${customerId}/accept-pending-changes`,
        method: 'POST',
        body,
      }),
    }),
    rejectPendingCustomerAudit: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string; reason: string }
    >({
      invalidatesTags: ['Audits', 'Customer'],
      query: ({ customerId, reason }) => ({
        url: `customers/${customerId}/reject-pending-changes`,
        method: 'POST',
        body: { reason },
      }),
    }),
    // My Customers
    addCustomerToMyCustomers: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string; agencyId?: number }
    >({
      query: ({ customerId, agencyId }) => ({
        url: `customers/${customerId}/users`,
        method: 'POST',
        body: { agencyId },
      }),
    }),
    removeCustomerFromMyCustomers: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string; agencyId?: number }
    >({
      query: ({ customerId, agencyId }) => ({
        url: `customers/${customerId}/users`,
        method: 'DELETE',
        body: agencyId !== undefined ? { agencyId } : undefined,
      }),
    }),
    setCustomerMyAgencyLinks: builder.mutation<
      Response<TMongoCustomer>,
      { customerId: string; agencyIds: (string | number)[] }
    >({
      invalidatesTags: ['Customer'],
      query: ({ customerId, agencyIds }) => ({
        url: `customers/${customerId}/users/my-agencies`,
        method: 'PATCH',
        body: { agencyIds },
      }),
    }),
    // Get options
    getCAECodes: builder.query<Response<TCAE>, TQueryParams>({
      query: (params) => formatQueryParams(params, 'customers/cae-codes'),
      transformResponse: parseJsonApiResponse<TCAE>,
    }),
    getActivitySectors: builder.query<Response<TActivitySector>, TQueryParams>({
      query: (params) =>
        formatQueryParams(params, 'customers/activity-sectors'),
      transformResponse: parseJsonApiResponse<TActivitySector>,
    }),
    getIRCTs: builder.query<Response<TIRCT>, TQueryParams>({
      query: (params) =>
        formatQueryParams(params, 'customers/customer-regulations'),
      transformResponse: parseJsonApiResponse<TIRCT>,
    }),
    getPaymentTypes: builder.query<Response<TPaymentType>, TQueryParams>({
      query: (params) => formatQueryParams(params, 'customers/payment-types'),
      transformResponse: parseJsonApiResponse<TPaymentType>,
    }),
    getPortalCountries: builder.query<Response<TPortalCountry>, TQueryParams>({
      query: (params) => formatQueryParams(params, 'customers/countries'),
      transformResponse: parseJsonApiResponse<TPortalCountry>,
    }),
    getCustomerTypes: builder.query<Response<TCustomerType>, TQueryParams>({
      query: (params) => formatQueryParams(params, 'customers/customer-types'),
      transformResponse: parseJsonApiResponse<TCustomerType>,
    }),
    searchAnadoluInformaCustomers: builder.query<
      Response<TInformaDbSearchHit>,
      TQueryParams
    >({
      providesTags: ['InformaDbSearchHit'],
      query: (query) =>
        formatQueryParams(query, 'anadolu/customers/informadb/search'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TInformaDbSearchHit>(
          res as JsonApiResponseDto<TInformaDbSearchHit>,
          meta,
          args,
          false,
        ),
    }),
    getAnadoluCustomersList: builder.query<
      Response<TAnadoluMongoCustomer>,
      TQueryParams
    >({
      providesTags: ['AnadoluCustomer'],
      query: (params) => formatQueryParams(params, 'anadolu/customers'),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TAnadoluMongoCustomer>(
          res as JsonApiResponseDto<TAnadoluMongoCustomer>,
          meta,
          args,
          true,
        ),
    }),
    getAnadoluCustomerByNif: builder.query<
      Response<TAnadoluMongoCustomer>,
      { nif: string }
    >({
      providesTags: ['AnadoluCustomer'],
      query: ({ nif }) => ({
        url: `anadolu/customers/nif/${encodeURIComponent(nif)}`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    getAnadoluCustomer: builder.query<
      Response<TAnadoluMongoCustomer>,
      { customerId: string }
    >({
      providesTags: ['AnadoluCustomer'],
      query: ({ customerId }) => ({
        url: `anadolu/customers/${customerId}`,
        method: 'GET',
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    createAnadoluCustomer: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customer: Record<string, unknown> }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customer }) => ({
        url: 'anadolu/customers',
        method: 'POST',
        body: customer,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    editAnadoluCustomer: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string; customer: Record<string, unknown> }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, customer }) => ({
        url: `anadolu/customers/${customerId}`,
        method: 'PATCH',
        body: customer,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    syncAnadoluCustomerWithInforma: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId }) => ({
        url: `anadolu/customers/${customerId}/sync-with-informa`,
        method: 'POST',
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    editAnadoluCustomerAddress: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      {
        customerId: string;
        addressId: string;
        address: Record<string, unknown>;
      }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, addressId, address }) => ({
        url: `anadolu/customers/${customerId}/addresses/${addressId}`,
        method: 'PATCH',
        body: address,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    addAnadoluCustomerAddress: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string; address: Record<string, unknown> }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, address }) => ({
        url: `anadolu/customers/${customerId}/addresses`,
        method: 'POST',
        body: address,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    excludeAnadoluCustomer: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string; excludedCause: string }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, excludedCause }) => ({
        url: `anadolu/customers/${customerId}/exclude`,
        method: 'POST',
        body: { excludedCause },
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    changeAnadoluCustomerStatus: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string; status: TCustomerStatus; excludedMotive?: string }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, status, excludedMotive }) => ({
        url: `anadolu/customers/${customerId}/status`,
        method: 'PATCH',
        body: { status, excludedMotive },
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    addAnadoluCustomerToMyCustomers: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string; agencyId?: number }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, agencyId }) => ({
        url: `anadolu/customers/${customerId}/users`,
        method: 'POST',
        body: agencyId != null ? { agencyId } : {},
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    removeAnadoluCustomerFromMyCustomers: builder.mutation<
      Response<TAnadoluMongoCustomer>,
      { customerId: string; agencyId?: number }
    >({
      invalidatesTags: ['AnadoluCustomer', 'Audits'],
      query: ({ customerId, agencyId }) => ({
        url: `anadolu/customers/${customerId}/users`,
        method: 'DELETE',
        body: agencyId != null ? { agencyId } : {},
      }),
      transformResponse: parseJsonApiResponse<TAnadoluMongoCustomer>,
    }),
    getAnadoluCustomerLogs: builder.query<
      Response<TCustomerAudit>,
      TQueryParams & { customerId: string }
    >({
      providesTags: ['Audits'],
      query: ({ customerId, ...params }) =>
        formatQueryParams(
          { ...params },
          `anadolu/customers/${customerId}/audit`,
        ),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TCustomerAudit>(
          res as JsonApiResponseDto<TCustomerAudit>,
          meta,
          args,
          true,
        ),
    }),
    getCustomerDocuments: builder.query<
      TCustomerDocumentsList,
      { customerId: string }
    >({
      providesTags: ['CustomerDocuments'],
      query: ({ customerId }) => ({
        url: `customers/${customerId}/documents`,
        method: 'GET',
      }),
      transformResponse: (res: any, meta, args) => {
        const parsed = parseJsonApiResponse<any>(res, meta, args);
        const row = parsed.values[0] as {
          data?: TCustomerDocumentsList;
          legacy?: TCustomerLegacyFile[];
          s3?: TCustomerDocument[];
        };
        const payload = row?.data ?? row;
        return {
          legacy: payload?.legacy ?? [],
          s3: payload?.s3 ?? [],
        };
      },
    }),
    getCustomerDocument: builder.mutation({
      queryFn: async (
        { customerId, documentId },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `customers/${customerId}/documents/${documentId}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          const url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          const blobUrl = url.createObjectURL(result.data);
          const hiddenElement = document.createElement('a');
          hiddenElement.href = blobUrl;
          hiddenElement.target = '_blank';
          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
    getCustomerLegacyDocument: builder.mutation({
      queryFn: async (
        { customerId, fileName },
        api,
        extraOptions,
        baseQuery,
      ) => {
        const result: any = await baseQuery({
          url: `customers/${customerId}/legacy-documents/${encodeURIComponent(fileName)}`,
          responseHandler: (response) => response.blob(),
        });
        try {
          const url = window.URL || window.webkitURL;
          if (!result.data) {
            return {
              error: 'Documento não encontrado',
            };
          }
          const blobUrl = url.createObjectURL(result.data);
          const hiddenElement = document.createElement('a');
          hiddenElement.href = blobUrl;
          hiddenElement.target = '_blank';
          hiddenElement.click();
        } catch (err: any) {
          return {
            error: err?.message,
          };
        }
        return { data: null };
      },
    }),
    uploadCustomerDocument: builder.mutation({
      invalidatesTags: ['CustomerDocuments'],
      query: ({
        customerId,
        file,
        type,
      }: {
        customerId: string;
        file: { file: File; filename: string }[];
        type?: TCustomerDocumentType;
      }) => {
        const document = file[0];
        const body = new FormData();
        body.append('document', document.file);
        body.append('originalName', document.filename);
        if (type) {
          body.append('type', type);
        }
        return {
          url: `customers/${customerId}/documents`,
          method: 'POST',
          body,
        };
      },
    }),
    deleteCustomerDocument: builder.mutation({
      invalidatesTags: ['CustomerDocuments'],
      query: ({ customerId, documentId }) => ({
        url: `customers/${customerId}/documents/${documentId}`,
        method: 'DELETE',
      }),
    }),
    getSyncedInformaNotifications: builder.query<
      Response<TInformaSyncedNotification>,
      TQueryParams
    >({
      query: (params) =>
        formatQueryParams(params, 'informadb/notifications/synced'),
      transformResponse: (res, meta, args) => {
        const parsed = parseJsonApiResponse<TInformaSyncedNotification>(
          res as JsonApiResponseDto<TInformaSyncedNotification>,
          meta,
          args,
          true,
        );
        const values = (parsed.values ?? []).map((row, index) => {
          const raw = row as TInformaSyncedNotification & {
            _id?: string | { toString(): string };
          };
          const mongoId =
            typeof raw._id === 'string'
              ? raw._id
              : raw._id != null
                ? String(raw._id)
                : undefined;
          const id =
            raw.id ??
            mongoId ??
            `${raw.alertDay ?? ''}-${raw.identifierValue ?? ''}-${raw.trigger ?? ''}-${raw.subTrigger ?? ''}-${index}`;
          return { ...raw, id };
        });
        return { ...parsed, values };
      },
    }),
  }),
});

export const {
  useCreateCustomerMutation,
  useGetCustomerQuery,
  useGetCustomerByNifQuery,
  useGetCustomersListQuery,
  useEditCustomerMutation,
  useUpdateCustomerFiscalNumberMutation,
  useValidateCustomerMutation,
  useActivateCustomerMutation,
  useChangeCustomerStatusMutation,
  useExcludeCustomerMutation,
  useSyncCustomerWithInformaMutation,
  useSearchCustomersQuery,
  useImportInformaDbCustomerMutation,
  useGetCustomerRecordMutation,
  useGetInformaReportMutation,
  useEditCustomerAddressMutation,
  useDeleteCustomerAddressMutation,
  useAddCustomerAddressMutation,
  useGetCustomerLogsQuery,
  useAcceptPendingCustomerAuditMutation,
  useRejectPendingCustomerAuditMutation,
  useAddCustomerToMyCustomersMutation,
  useRemoveCustomerFromMyCustomersMutation,
  useSetCustomerMyAgencyLinksMutation,
  useGetCAECodesQuery,
  useGetActivitySectorsQuery,
  useGetIRCTsQuery,
  useGetPaymentTypesQuery,
  useGetPortalCountriesQuery,
  useGetCustomerTypesQuery,
  useGetAnadoluCustomersListQuery,
  useSearchAnadoluInformaCustomersQuery,
  useGetAnadoluCustomerByNifQuery,
  useLazyGetAnadoluCustomerByNifQuery,
  useGetAnadoluCustomerQuery,
  useCreateAnadoluCustomerMutation,
  useEditAnadoluCustomerMutation,
  useSyncAnadoluCustomerWithInformaMutation,
  useEditAnadoluCustomerAddressMutation,
  useAddAnadoluCustomerAddressMutation,
  useExcludeAnadoluCustomerMutation,
  useChangeAnadoluCustomerStatusMutation,
  useAddAnadoluCustomerToMyCustomersMutation,
  useRemoveAnadoluCustomerFromMyCustomersMutation,
  useGetAnadoluCustomerLogsQuery,
  useGetCustomerDocumentsQuery,
  useGetCustomerDocumentMutation,
  useGetCustomerLegacyDocumentMutation,
  useUploadCustomerDocumentMutation,
  useDeleteCustomerDocumentMutation,
  useGetSyncedInformaNotificationsQuery,
} = customersApi;
